import {
	Eventcalendar,
	MbscCalendarEvent,
	MbscResource,
	momentTimezone,
	setOptions,
} from '@mobiscroll/react';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { fetchAllEventTypes, deleteEvent } from 'core/API/events';
import { GetUsersList } from 'core/API/users';
// CSS modules
import Loading from 'core/components/Loading/Loading';
import { useAuth } from 'module/auth/context/AuthContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

import { useCalendarView } from 'core/hooks/useCalendarView';
import { ScheduleEvent, ScheduleResource } from './DesktopPage.component';
import { EventsHeader } from 'module/events/components/EventsHeader';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText
} from '@mui/material';
import './DesktopPage.style.scss';
import { debounceFunction } from 'core/hooks/useDecbouncedValue';
import { User } from '../../../../types';

// setup Mobiscroll Moment plugin
momentTimezone.moment = momentTZ;

interface IDesktopPage {
	openEditShiftModal: (args: any) => void;
	openAddShiftModal: (args: any) => void;
	allSchedules: MbscCalendarEvent[];
	allEvents: MbscCalendarEvent[];
	workplacesData: IWorkplaces;
	updateTimeRange: (firstday: Date, lastday: Date) => void;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	calendarType: string;
	openRequestsModal: (args: any) => void;
	openAddEventModal: (args: any) => void;
	openEditEventModal: (args: any) => void;
	reassignShiftToAnotherUser: (newShift: MbscCalendarEvent) => void;
	refetch: any;
	updateOneShiftHandler: (selectedEvent: MbscCalendarEvent) => void;
	dragTimeStep?: number;
	resourcesOverride?: MbscResource[];
	renderHeaderOverride?: () => React.ReactElement | null;
	selectedDate?: string; // ISO format: YYYY-MM-DD
	canEditEvents?: boolean; // Permission to edit events
	canDeleteEvents?: boolean; // Permission to delete events
}

setOptions({
	theme: 'ios',
	themeVariant: 'light',
});

const DesktopPage: FC<IDesktopPage> = ({
	openEditShiftModal,
	openAddShiftModal,
	allSchedules,
	allEvents,
	workplacesData,
	updateTimeRange,
	setCalendarType,
	calendarType,
	openRequestsModal,
	openAddEventModal,
	openEditEventModal,
	reassignShiftToAnotherUser,
	refetch,
	updateOneShiftHandler,
	dragTimeStep,
	resourcesOverride,
	renderHeaderOverride,
	selectedDate,
	canEditEvents = true, // Default to true for backward compatibility
	canDeleteEvents = true, // Default to true for backward compatibility
}) => {
	const SCROLL_STORAGE_KEY = 'events-calendar-scroll';
	const SCROLL_LEFT_STORAGE_KEY = 'events-calendar-scroll-left';
	const SCROLL_RESTORE_FLAG = 'events-calendar-restore-scroll';
	const { workplaceId, timezones } = useWorkplacesContext();
	const { calendarView, calendarConfig } = useCalendarView();

	// Configure calendar for single day view when calendarView is 'day'
	const dayCalendarConfig = useMemo(() => {
		if (calendarView === 'day' && selectedDate) {
			const selectedMoment = moment(selectedDate);
			return {
				...calendarConfig,
				timeline: {
					type: 'day' as const,
					timeCellStep: 60,
					timeLabelStep: 60,
					rowHeight: 'variable' as const,
				},
			};
		}
		return calendarConfig;
	}, [calendarView, selectedDate, calendarConfig]);
	console.log('here is workplace id', workplaceId);
	const auth = useAuth();
	const queryClient = useQueryClient();
	const { showToast } = useToastContext();

	const [invalidDates, setInvalidDates] = useState<any>();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [eventToDelete, setEventToDelete] = useState<MbscCalendarEvent | null>(null);

	const { user } = JSON.parse(localStorage.getItem('BYOD-app'));
	const {
		data: usersData,
		isLoading: isLoadingUsersWorkplace,
		refetch: refetchUsers,
	} = useQuery(['users', user.id], () => GetUsersList(user.id), {
		enabled: calendarType === 'schedule' || calendarType === 'request',
		select: (data: User[]) => {
			function filterAndPreserveDuplicates(users: User[]) {
				const uniqueIds = new Set();
				const duplicates = [];

				const uniques = users.filter((user) => {
					if (uniqueIds.has(user.id)) {
						debounceFunction(() => {
							console.group('Duplicate user!');
							console.log('Found duplicate user: ', user);
							console.log(
								'Original user: ',
								users.find((u) => u.id === user.id)
							);
							console.groupEnd();
						}, 1000);

						duplicates.push(user); // Add duplicate to duplicates array
						return false; // If ID is duplicate, filter it out
					} else {
						uniqueIds.add(user.id); // Add ID to uniqueIds Set
						return true; // Keep the user
					}
				});

				return { uniques, duplicates };
			}

			const { uniques } = filterAndPreserveDuplicates(data);

			return uniques;
		},
	});

	const { data: eventTypes } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
		enabled: calendarType === 'event',
	});

	const getScrollContainer = useCallback(() => {
		const selectors = [
			'.mbsc-timeline-grid-scroll',
			'.mbsc-schedule-grid-scroll',
			'.mbsc-timeline-resource-scroll',
			'.mbsc-schedule-resource-scroll',
		];
		for (const selector of selectors) {
			const el = document.querySelector(selector) as HTMLElement | null;
			if (el) return el;
		}
		return null;
	}, []);

	const saveScrollPosition = useCallback(() => {
		if (calendarType !== 'event') return;
		const scrollContainer = getScrollContainer();
		if (!scrollContainer) return;
		localStorage.setItem(SCROLL_STORAGE_KEY, String(scrollContainer.scrollTop));
		localStorage.setItem(SCROLL_LEFT_STORAGE_KEY, String(scrollContainer.scrollLeft));
		localStorage.setItem(SCROLL_RESTORE_FLAG, '1');
	}, [calendarType, getScrollContainer]);

	const restoreScrollPosition = useCallback(() => {
		if (calendarType !== 'event') return;
		if (localStorage.getItem(SCROLL_RESTORE_FLAG) !== '1') return;
		const savedTop = localStorage.getItem(SCROLL_STORAGE_KEY);
		if (!savedTop) return;
		const scrollContainer = getScrollContainer();
		if (!scrollContainer) return;
		const top = Number(savedTop);
		const left = Number(localStorage.getItem(SCROLL_LEFT_STORAGE_KEY) || '0');
		if (!Number.isNaN(top)) scrollContainer.scrollTop = top;
		if (!Number.isNaN(left)) scrollContainer.scrollLeft = left;
	}, [calendarType, getScrollContainer]);

	useEffect(() => {
		if (allSchedules.length) {
			const tempArrayOfInvalidDates = [];
			allSchedules.forEach((shift) => {
				if (shift?.shift_type?.id === 2 && shift?.scheduled) {
					tempArrayOfInvalidDates.push({
						start: moment(shift.start).toDate(),
						end: moment(shift.end).toDate(),
						title: 'Time off',
						resource: shift.resource,
					});
				}
			});
			setInvalidDates(tempArrayOfInvalidDates);
			// Removed but might be required:
			// refetchUsers();
		}
	}, [allSchedules, refetchUsers]);

	const myResources = useMemo<MbscResource[]>(() => {
		const usersListResource: MbscResource[] = [];
		const firstUser: MbscResource[] = [];
		usersData?.forEach(
			(value) => {
				if (value.id === auth.user.id) {
					firstUser.push({
						id: value.id,
						name: `${value.firstName} ${value.lastName}`,
						role: '',
						active_week_hours: '',
					});
				} else {
					usersListResource.push({
						id: value.id,
						name: `${value.firstName} ${value.lastName}`,
						role: '',
						active_week_hours: '',
					});
				}
			},
			[usersData]
		);

		// Sort users primarily by last name (ascending), then by active_week_hours (descending)
		usersListResource.sort((a, b) => {
			const getLastName = (fullName: string) => {
				if (!fullName) return '';
				const parts = fullName.trim().split(' ');
				return parts[parts.length - 1].toLowerCase();
			};

			const lastA = getLastName(a.name as string);
			const lastB = getLastName(b.name as string);

			if (lastA < lastB) return -1;
			if (lastA > lastB) return 1;

			// If last names equal, sort by hours scheduled (descending)
			const hoursA = Number(a.active_week_hours ?? 0);
			const hoursB = Number(b.active_week_hours ?? 0);

			return hoursB - hoursA;
		});

		// sort by roles

		return [...firstUser, ...usersListResource]!;
	}, [usersData, auth.user.id]);

	const eventResources = useMemo<MbscResource[]>(() => {
		const eventsListResource = eventTypes?.items?.map((eventType: any) => {
			return {
				id: eventType.id,
				name: eventType.type,
			};
		});
		return eventsListResource;
	},[eventTypes]);
	const singleEventResource = useMemo<MbscResource[]>(() => [{
		id: 'events-row',
		name: 'Events'
	}], []);

	// Create a stable key from event resources to prevent unnecessary recalculations
	const eventsResourceKey = useMemo(() => {
		if (calendarType !== 'event' || !allEvents || allEvents.length === 0) {
			return '';
		}
		// Create a stable string key from all resource IDs
		const resourceIds = allEvents
			.map((e: any) => e.resource)
			.filter(Boolean)
			.sort()
			.join(',');
		return resourceIds;
	}, [allEvents, calendarType]);

	// Create dynamic row resources based on events - memoized and sorted for stability
	const dynamicRowResources = useMemo<MbscResource[]>(() => {
		if (calendarType !== 'event' || !allEvents || allEvents.length === 0 || !eventsResourceKey) {
			return [];
		}

		const resourceMap = new Map<string | number, { resource: MbscResource; rowNumber: number; baseResource: string | number }>();

		// Create a stable set of resource IDs from events
		const eventResourceIds = new Set<string | number>();
		allEvents.forEach((event: any) => {
			if (event.resource) {
				eventResourceIds.add(event.resource);
			}
		});

		// Add all dynamic row resources from events - sorted for consistency
		const sortedResourceIds = Array.from(eventResourceIds).sort((a, b) => {
			const aStr = String(a);
			const bStr = String(b);
			return aStr.localeCompare(bStr);
		});

		sortedResourceIds.forEach((resourceId) => {
			if (!resourceMap.has(resourceId)) {
				// Check if it's a row resource (contains '-row-')
				const resourceIdStr = String(resourceId);
				if (resourceIdStr.includes('-row-')) {
					// Extract base resource ID and row number
					const parts = resourceIdStr.split('-row-');
					const baseResourceIdStr = parts[0];
					const rowNumber = parseInt(parts[1] || '0', 10);
					
					// Use empty name to hide the label
					resourceMap.set(resourceId, {
						resource: {
							id: resourceId,
							name: '', // Empty name to hide resource label
						},
						rowNumber: rowNumber,
						baseResource: baseResourceIdStr,
					});
				} else {
					// Handle base resource (shouldn't happen with new logic, but keep for safety)
					const baseResource = eventResources?.find(r => r.id === resourceId);
					resourceMap.set(resourceId, {
						resource: {
							id: resourceId,
							name: baseResource?.name || 'Events',
						},
						rowNumber: 0,
						baseResource: resourceId,
					});
				}
			}
		});

		// Sort resources by base resource, then by row number for consistent ordering
		const sortedResources = Array.from(resourceMap.values()).sort((a, b) => {
			const baseCompare = String(a.baseResource).localeCompare(String(b.baseResource));
			if (baseCompare !== 0) return baseCompare;
			return a.rowNumber - b.rowNumber;
		});

		return sortedResources.map(item => item.resource);
	}, [eventsResourceKey, eventResources, calendarType]);

	// Prefer explicit override, then dynamic row resources, then event types, then defaults
	// For events, ensure at least one blank resource row is shown (similar to Schedule)
	const resourcesToRender = resourcesOverride || (calendarType === 'event' 
		? (dynamicRowResources.length > 0 ? dynamicRowResources : singleEventResource)
		: eventResources);

	// For visualization: create dummy shifts for each job code when in 'event' calendar type
	const dataForCalendar = allEvents;

	const compareEvents = (selectedEvent: MbscCalendarEvent) => {
		allSchedules.forEach((schedule) => {
			if (schedule.id === selectedEvent.id) {
				if (selectedEvent.resource !== schedule.resource) {
					// check for roles of the other user and update shift

					reassignShiftToAnotherUser(selectedEvent);
				} else {
					// open modal for update shift
					// openEditShiftModal(selectedEvent);
					// call function to update shift
					updateOneShiftHandler(selectedEvent);
				}
			}
		});
	};

	const disableDeleteKey = () => {
		refetch();
	};

	// Delete event mutation
	const { mutate: deleteEventMutation, isLoading: isDeletingEvent } = useMutation(deleteEvent, {
		onSuccess: () => {
			showToast('success', 'Event successfully deleted!');
			queryClient.invalidateQueries(['events']);
			setIsDeleteDialogOpen(false);
			setEventToDelete(null);
			refetch();
		},
		onError: () => {
			showToast('error', 'Failed to delete event');
			setIsDeleteDialogOpen(false);
			setEventToDelete(null);
		},
	});

	// Handle delete click - show confirmation dialog
	const handleDeleteClick = (event: MbscCalendarEvent) => {
		if (!canDeleteEvents) {
			showToast('error', 'You do not have permission to delete events');
			return;
		}
		setEventToDelete(event);
		setIsDeleteDialogOpen(true);
	};

	// Handle confirm delete - call API
	const handleConfirmDelete = () => {
		if (eventToDelete) {
			// Extract event ID from originalEvent (which contains the actual API event object)
			const eventId = eventToDelete.originalEvent?.id || eventToDelete.original?.id;
			if (eventId) {
				// Handle both string and number IDs
				const numericId = typeof eventId === 'string' ? parseInt(eventId) : eventId;
				if (numericId && !isNaN(numericId)) {
					deleteEventMutation(numericId);
				}
			}
		}
	};

	const handleEditEvent = useCallback(
		(event: MbscCalendarEvent) => {
			saveScrollPosition();
			openEditEventModal(event);
		},
		[openEditEventModal, saveScrollPosition]
	);

	const handleAddEvent = useCallback(
		(event: any) => {
			saveScrollPosition();
			openAddEventModal(event);
		},
		[openAddEventModal, saveScrollPosition]
	);

	useEffect(() => {
		if (calendarType !== 'event') return;
		if (localStorage.getItem(SCROLL_RESTORE_FLAG) !== '1') return;
		let ticks = 0;
		const maxTicks = 30; // ~3s at 100ms
		const intervalId = window.setInterval(() => {
			restoreScrollPosition();
			ticks += 1;
			if (ticks >= maxTicks) {
				localStorage.removeItem(SCROLL_RESTORE_FLAG);
				window.clearInterval(intervalId);
			}
		}, 100);
		return () => {
			window.clearInterval(intervalId);
		};
	}, [calendarType, allEvents, selectedDate, calendarView, restoreScrollPosition]);

	return isLoadingUsersWorkplace ? (
		<Loading />
	) : (
		<>
		<Eventcalendar
			invalid={invalidDates}
			invalidateEvent="strict"
			eventOverlap={true}
			eventOverlapThreshold={0}
			theme="ios"
			dataTimezone="local"
			displayTimezone="local"
			timezonePlugin={momentTimezone}
			themeVariant="light"
			clickToCreate={true}
			// dragToCreate={calendarType !== 'request'}
			dragToCreate={false}
			dragToMove={calendarType === 'event' ? !!canEditEvents : true}
			dragToResize={calendarType === 'event' ? !!canEditEvents : calendarView !== 'week'} //disable resize shift only on week view
			view={dayCalendarConfig}
			selectedDate={selectedDate ? moment(selectedDate).toDate() : undefined}
			data={calendarType === 'event' ? dataForCalendar : allSchedules}
			renderScheduleEvent={(event) => (
				<ScheduleEvent 
					calendarType={calendarType} 
					data={event} 
					onEditClick={calendarType === 'event' && canEditEvents ? handleEditEvent : undefined}
					onDeleteClick={calendarType === 'event' && canDeleteEvents ? handleDeleteClick : undefined}
					canEditEvents={calendarType === 'event' ? canEditEvents : true}
					canDeleteEvents={calendarType === 'event' ? canDeleteEvents : true}
				/>
			)}
			renderResource={(resource) => (
				<ScheduleResource resource={resource} calendarType={calendarType} />
			)}
			// invalid={myInvalids}
			resources={
				calendarType === 'event' || calendarType === 'request'
					? resourcesToRender
					: myResources
			}
			resourceAreaWidth={calendarType === 'event' ? '15%' : undefined}
			resourceAreaHeaderContent={calendarType === 'event' ? '' : undefined}
			renderHeader={() => {
				// If parent provided an override, use it.
				if (renderHeaderOverride) {
					return renderHeaderOverride();
				}

				// Use EventsHeader-style UI (day/week toggle) as the default header for better parity with Events page.
				return (
					<EventsHeader
						workplacesData={workplacesData}
						workplaceId={workplaceId}
						onSwitchCalendarType={setCalendarType}
					/>
				);
			}}
			// use 15 minute increments for finer resolution
			// allow parent to override drag step (defaults to 15)
			dragTimeStep={dragTimeStep ?? 15}
			cssClass={`md-switching-timeline-view-cont md-timeline-height ${calendarType === 'event' ? 'events-page-calendar-timeline' : ''} ${
				calendarType === 'event' && !canEditEvents ? 'events-readonly' : ''
			}`}
			timeFormat="HH:mm"
			externalDrop={true}
			// eventOrder={orderMyEvents}
			onCellClick={(event) => {
				switch (calendarType) {
					case 'event':
						handleAddEvent(event);
						break;
					default:
						openAddShiftModal(event);
						break;
				}
			}}
			onEventDoubleClick={({ event }) => {
				/* Logic for event double click */
				if (event.role || !event.scheduled) {
					// if no role its timeoff disable popup on timeoff
					switch (calendarType) {
						case 'request':
							openRequestsModal(event);
							break;
						case 'event':
							if (!canEditEvents) {
								return;
							}
							handleEditEvent({
								...event,
								end: new Date(event.end ? event.end.toString().split('+')[0] : '').toString(),
								start: new Date(event.start ? event.start.toString().split('+')[0] : '').toString(),
							});
							break;
						default:
							openEditShiftModal(event);
							break;
					}
				}
			}}
			onEventUpdate={({ event }) => {
				/* Logic for event update */
				switch (calendarType) {
					case 'request':
						openRequestsModal(event);
						break;
					case 'event':
						if (!canEditEvents) {
							return;
						}
						handleEditEvent({
							...event,
							fromUtc: moment(event?.start).format('HH:mm'),
							toUtc: moment(event?.end).format('HH:mm'),
						});
						break;
					default:
						compareEvents(event);
						break;
				}
			}}
			onPageChange={(event) => {
				/* Your custom event handler goes here */
				// setFromDate(formatDate(event.firstDay));
				// setToDate(formatDate(event.lastDay));
				updateTimeRange(event.firstDay, event.lastDay);
			}}
			onEventCreate={(event) => {
				if (event.action === 'drag') return;

				if (event.action === 'externalDrop') {
					openEditShiftModal(event.event);
				} else {
					openAddShiftModal(event);
				}
			}}
			onEventClick={() => {
				disableDeleteKey();
			}}
		/>
		<Dialog
			open={isDeleteDialogOpen}
			onClose={() => {
				setIsDeleteDialogOpen(false);
				setEventToDelete(null);
			}}
			aria-labelledby="delete-dialog-title"
			aria-describedby="delete-dialog-description"
			maxWidth="sm"
			fullWidth
			style={{ zIndex: 9999 }}
			PaperProps={{
				style: {
					backgroundColor: 'white',
					boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
					borderRadius: '8px',
					minWidth: '400px'
				}
			}}
		>
			<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText id="delete-dialog-description">
					Are you sure you want to delete the event{' '}
					{eventToDelete && (
						<strong>&ldquo;{eventToDelete.name || eventToDelete.title || 'this event'}&rdquo;</strong>
					)}? This action cannot be undone.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => {
						setIsDeleteDialogOpen(false);
						setEventToDelete(null);
					}}
					style={{ color: 'black' }}
					disabled={isDeletingEvent}
				>
					Cancel
				</Button>
				<Button
					style={{ backgroundColor: 'red', color: 'white' }}
					onClick={handleConfirmDelete}
					disabled={isDeletingEvent}
				>
					{isDeletingEvent ? 'Deleting...' : 'Delete'}
				</Button>
			</DialogActions>
		</Dialog>
	</>
	);
};

export default DesktopPage;
